import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, NavLink, Outlet, useLocation, useMatch, useNavigate } from 'react-router';
import { useLogout, useMe, useUpdateMe, useWorkspace, useWorkspaces } from '../api/queries';
import type { Locale } from '../api/types';
import { Select } from '../components/ui';
import { LOCALES } from '../i18n';
import { cn } from '../lib/cn';
import { applyTheme, readTheme, saveTheme, type ThemePreference } from '../lib/theme';
import { useLocale } from './hooks';

export function AppShell() {
  const { t } = useTranslation();
  const location = useLocation();
  // The menu (below lg) is open for the page it was opened on; navigating closes it.
  const [openOn, setOpenOn] = useState<string | null>(null);
  const menuOpen = openOn === location.pathname;
  const setMenuOpen = (open: boolean) => setOpenOn(open ? location.pathname : null);
  const match = useMatch('/w/:workspaceId/*');
  const workspaceId = match?.params.workspaceId;

  return (
    <div className="min-h-dvh lg:grid lg:grid-cols-[15rem_1fr]">
      <a
        href="#main"
        className="sr-only z-50 rounded bg-white px-3 py-2 focus:not-sr-only focus:fixed focus:top-2 focus:left-2 dark:bg-slate-900"
      >
        {t('nav.skipToContent')}
      </a>
      {/* The column carries the sidebar background, so it reaches the bottom of long pages
          (and full-page screenshots); the sidebar itself stays sticky at viewport height. */}
      <div className="contents lg:block lg:border-r lg:border-slate-200 lg:bg-white dark:lg:border-slate-800 dark:lg:bg-slate-900">
        <aside
          className={cn(
            'bg-white dark:bg-slate-900',
            'lg:sticky lg:top-0 lg:flex lg:h-dvh lg:flex-col',
            menuOpen ? 'fixed inset-0 z-40 flex flex-col' : 'hidden',
          )}
          aria-label={t('nav.primary')}
        >
          <div className="flex h-14 items-center justify-between gap-2 border-b border-slate-100 px-4 dark:border-slate-800">
            <Logo />
            <button
              type="button"
              className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 lg:hidden dark:hover:bg-slate-800"
              onClick={() => setMenuOpen(false)}
              aria-label={t('nav.closeMenu')}
            >
              <Icon d="M6 6l12 12M18 6 6 18" />
            </button>
          </div>
          <div className="border-b border-slate-100 p-3 dark:border-slate-800">
            <WorkspaceSwitcher current={workspaceId} />
          </div>
          <nav className="flex-1 overflow-y-auto p-3">
            {workspaceId ? (
              <WorkspaceNav ws={workspaceId} />
            ) : (
              <p className="px-2 text-xs text-slate-500">{t('nav.noWorkspace')}</p>
            )}
          </nav>
        </aside>
      </div>

      <div className="flex min-w-0 flex-col">
        <header className="sticky top-0 z-30 flex h-14 items-center gap-2 border-b border-slate-200 bg-white/90 px-4 backdrop-blur dark:border-slate-800 dark:bg-slate-900/90">
          <button
            type="button"
            className="-ml-1 rounded-md p-1.5 text-slate-600 hover:bg-slate-100 lg:hidden dark:text-slate-300 dark:hover:bg-slate-800"
            onClick={() => setMenuOpen(true)}
            aria-label={t('nav.openMenu')}
            aria-expanded={menuOpen}
          >
            <Icon d="M4 7h16M4 12h16M4 17h16" />
          </button>
          <div className="lg:hidden">
            <Logo />
          </div>
          <div className="ml-auto flex items-center gap-1.5">
            <FeedbackLink />
            <HelpLink />
            <LocaleSwitch />
            <ThemeToggle />
            <UserMenu />
          </div>
        </header>
        <main id="main" className="mx-auto w-full max-w-7xl flex-1 px-4 py-6 sm:px-6">
          <Outlet />
        </main>
        <footer className="px-4 pb-6 text-center sm:px-6">
          <Copyright />
        </footer>
      </div>
    </div>
  );
}

export function Logo() {
  return (
    <Link
      to="/"
      className="flex items-center gap-2 font-semibold tracking-tight text-slate-900 dark:text-white"
    >
      <img src="/logo.png" alt="" className="size-7" />
      <span>Smoke</span>
    </Link>
  );
}

function Icon({ d, className }: { d: string; className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className ?? 'size-5'}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={d} />
    </svg>
  );
}

const ICONS = {
  dashboard: 'M4 13h6V4H4v9Zm0 7h6v-5H4v5Zm10 0h6v-9h-6v9Zm0-16v5h6V4h-6Z',
  findings: 'M12 3 3 7v5c0 5 3.8 8.4 9 9 5.2-.6 9-4 9-9V7l-9-4Zm0 6v4m0 3h.01',
  clients: 'M3 21V7l9-4 9 4v14M9 21v-6h6v6M8 10h.01M12 10h.01M16 10h.01',
  agents: 'M8 9l-4 3 4 3m8-6 4 3-4 3M14 5l-4 14',
  eol: 'M12 7v5l3 2m6-2a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z',
  members:
    'M16 19v-1a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v1m14-8a3 3 0 1 0 0-6m6 14v-1a4 4 0 0 0-3-3.9M9 11a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z',
  settings:
    'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Zm7.4-3a7.4 7.4 0 0 0-.1-1.2l2-1.6-2-3.4-2.4 1a7.6 7.6 0 0 0-2-1.2L14.5 3h-5l-.4 2.6a7.6 7.6 0 0 0-2 1.2l-2.4-1-2 3.4 2 1.6a7.4 7.4 0 0 0 0 2.4l-2 1.6 2 3.4 2.4-1a7.6 7.6 0 0 0 2 1.2l.4 2.6h5l.4-2.6a7.6 7.6 0 0 0 2-1.2l2.4 1 2-3.4-2-1.6c.1-.4.1-.8.1-1.2Z',
  notifications: 'M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9Zm-4.3 13a2 2 0 0 1-3.4 0',
  tokens: 'M15 7a4 4 0 1 1-3.9 5H7v3H4v-3H3v-2h8.1A4 4 0 0 1 15 7Z',
  reports: 'M7 3h7l5 5v13H7V3Zm7 0v5h5M10 13h6M10 17h6',
  github:
    'M9 19c-4 1.3-4-2-6-2.5m12 4.5v-3.5a3 3 0 0 0-.8-2.3c2.7-.3 5.5-1.3 5.5-6a4.7 4.7 0 0 0-1.3-3.2 4.3 4.3 0 0 0-.1-3.2s-1-.3-3.4 1.3a11.6 11.6 0 0 0-6 0C6.6 2.5 5.6 2.8 5.6 2.8a4.3 4.3 0 0 0-.1 3.2A4.7 4.7 0 0 0 4.2 9.2c0 4.6 2.8 5.7 5.5 6a3 3 0 0 0-.8 2.3V21',
  billing: 'M3 7h18v10H3V7Zm0 4h18M7 15h3',
  audit:
    'M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2M9 5a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2M9 5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2m-6 9 2 2 4-4',
};

function NavItem({
  to,
  icon,
  children,
  end,
}: {
  to: string;
  icon: string;
  children: ReactNode;
  end?: boolean;
}) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-sm font-medium',
          isActive
            ? 'bg-brand-50 text-brand-800 dark:bg-brand-950/70 dark:text-brand-200'
            : 'text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800',
        )
      }
    >
      <Icon d={icon} className="size-4.5 shrink-0 opacity-80" />
      <span>{children}</span>
    </NavLink>
  );
}

function WorkspaceNav({ ws }: { ws: string }) {
  const { t } = useTranslation();
  const base = `/w/${ws}`;
  const role = useWorkspace(ws).data?.role;
  const isAdmin = role === 'admin' || role === 'owner';
  return (
    <div className="space-y-5">
      <ul className="space-y-0.5">
        <li>
          <NavItem to={base} icon={ICONS.dashboard} end>
            {t('nav.dashboard')}
          </NavItem>
        </li>
        <li>
          <NavItem to={`${base}/findings`} icon={ICONS.findings}>
            {t('nav.findings')}
          </NavItem>
        </li>
        <li>
          <NavItem to={`${base}/clients`} icon={ICONS.clients}>
            {t('nav.clients')}
          </NavItem>
        </li>
        <li>
          <NavItem to={`${base}/mcp`} icon={ICONS.agents}>
            {t('nav.agents')}
          </NavItem>
        </li>
        <li>
          <NavItem to={`${base}/eol`} icon={ICONS.eol}>
            {t('nav.eol')}
          </NavItem>
        </li>
        {isAdmin && (
          <li>
            <NavItem to={`${base}/reports`} icon={ICONS.reports}>
              {t('nav.reports')}
            </NavItem>
          </li>
        )}
      </ul>
      <div>
        <p className="mb-1 px-2.5 text-xs font-semibold text-slate-500 dark:text-slate-400">
          {t('nav.workspaceSection')}
        </p>
        <ul className="space-y-0.5">
          <li>
            <NavItem to={`${base}/members`} icon={ICONS.members}>
              {t('nav.members')}
            </NavItem>
          </li>
          <li>
            <NavItem to={`${base}/settings`} icon={ICONS.settings} end>
              {t('nav.settings')}
            </NavItem>
          </li>
          <li>
            <NavItem to={`${base}/settings/notifications`} icon={ICONS.notifications}>
              {t('nav.notifications')}
            </NavItem>
          </li>
          <li>
            <NavItem to={`${base}/api-tokens`} icon={ICONS.tokens}>
              {t('nav.apiTokens')}
            </NavItem>
          </li>
          {isAdmin && (
            <li>
              <NavItem to={`${base}/integrations/github`} icon={ICONS.github}>
                {t('nav.github')}
              </NavItem>
            </li>
          )}
          <li>
            <NavItem to={`${base}/audit`} icon={ICONS.audit}>
              {t('nav.audit')}
            </NavItem>
          </li>
          <li>
            <NavItem to={`${base}/billing/plans`} icon={ICONS.billing}>
              {t('nav.plans')}
            </NavItem>
          </li>
        </ul>
      </div>
    </div>
  );
}

function WorkspaceSwitcher({ current }: { current: string | undefined }) {
  const { t } = useTranslation();
  const workspaces = useWorkspaces();
  const navigate = useNavigate();
  const items = workspaces.data?.items ?? [];
  // Staff reading a workspace through an access grant are not members (FR-ADM-03).
  const viewed = useWorkspace(current ?? '').data;
  const outside = current && viewed && !items.some((w) => w.id === current) ? viewed : null;
  return (
    <div>
      <label
        htmlFor="workspace-switcher"
        className="mb-1 block text-xs font-medium text-slate-500 dark:text-slate-400"
      >
        {t('nav.workspace')}
      </label>
      <Select
        id="workspace-switcher"
        value={current ?? ''}
        onChange={(e) => {
          const v = e.target.value;
          void navigate(v === '__new' ? '/workspaces/new' : `/w/${v}`);
        }}
      >
        {!current && <option value="">{t('nav.chooseWorkspace')}</option>}
        {outside && <option value={outside.id}>{outside.name}</option>}
        {items.map((w) => (
          <option key={w.id} value={w.id}>
            {w.name}
          </option>
        ))}
        <option value="__new">{t('nav.createWorkspace')}</option>
      </Select>
    </div>
  );
}

/** The copyright line at the bottom of every page; the static help and legal pages carry it too. */
export function Copyright({ className }: { className?: string }) {
  return (
    <p className={cn('text-xs text-slate-500 dark:text-slate-400', className)}>
      © 2026 Aircraft - Smoke
    </p>
  );
}

/** The public data sources and licenses page (public/help), in the interface language. */
export function DataSourcesLink({ className }: { className?: string }) {
  const { t } = useTranslation();
  const locale = useLocale();
  return (
    <a
      href={`/help/data-sources.${locale}.html`}
      target="_blank"
      rel="noopener"
      className={className}
    >
      {t('nav.dataSources')}
    </a>
  );
}

export type LegalPage = 'terms' | 'privacy' | 'refund';

/**
 * One of the public legal pages (public/legal), in the interface language, in a new tab. Children
 * replace the page name (e.g. inside a translated sentence).
 */
export function LegalLink({
  page,
  className,
  children,
}: {
  page: LegalPage;
  className?: string;
  children?: ReactNode;
}) {
  const { t } = useTranslation();
  const locale = useLocale();
  return (
    <a href={`/legal/${page}.${locale}.html`} target="_blank" rel="noopener" className={className}>
      {children ?? t(`legal.${page}`)}
    </a>
  );
}

/** Terms, privacy and refund policy, for the footers of public pages. */
export function LegalLinks({ className }: { className?: string }) {
  return (
    <>
      {(['terms', 'privacy', 'refund'] as const).map((p) => (
        <LegalLink key={p} page={p} className={className} />
      ))}
    </>
  );
}

/** FR-ACC-13: the feedback page, told which page the user was on. */
export function FeedbackLink() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const to =
    pathname === '/feedback' ? '/feedback' : `/feedback?from=${encodeURIComponent(pathname)}`;
  return (
    <Link
      to={to}
      title={t('nav.feedbackTitle')}
      aria-label={t('nav.feedbackTitle')}
      className="flex items-center gap-1 rounded-md px-1.5 py-1 text-sm text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
    >
      <Icon
        d="M21 12a8 8 0 0 1-11.6 7.1L4 20l1-4.4A8 8 0 1 1 21 12ZM8.5 12h.01M12 12h.01M15.5 12h.01"
        className="size-4.5"
      />
      <span className="hidden sm:inline">{t('nav.feedback')}</span>
    </Link>
  );
}

/** The user guide (public/help), in the interface language, in a new tab. */
export function HelpLink() {
  const { t } = useTranslation();
  const locale = useLocale();
  return (
    <a
      href={`/help/${locale}.html`}
      target="_blank"
      rel="noopener"
      title={t('nav.helpTitle')}
      aria-label={t('nav.helpTitle')}
      className="flex items-center gap-1 rounded-md px-1.5 py-1 text-sm text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
    >
      <Icon
        d="M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Zm-2.5-11.5a2.5 2.5 0 1 1 3.5 2.3c-.6.3-1 .9-1 1.6V14m0 3h.01"
        className="size-4.5"
      />
      <span className="hidden sm:inline">{t('nav.help')}</span>
    </a>
  );
}

export function LocaleSwitch({ persist = true }: { persist?: boolean }) {
  const { t, i18n } = useTranslation();
  const locale = useLocale();
  const update = useUpdateMe();
  return (
    <div>
      <label htmlFor="locale-switch" className="sr-only">
        {t('nav.language')}
      </label>
      <Select
        id="locale-switch"
        className="h-8 w-auto py-0 text-xs"
        value={locale}
        onChange={(e) => {
          const next = e.target.value as Locale;
          void i18n.changeLanguage(next);
          if (persist) update.mutate({ locale: next });
        }}
      >
        {LOCALES.map((l) => (
          <option key={l} value={l}>
            {l === 'en' ? 'English' : '繁體中文'}
          </option>
        ))}
      </Select>
    </div>
  );
}

export function ThemeToggle() {
  const { t } = useTranslation();
  const [pref, setPref] = useState<ThemePreference>(() => readTheme());
  useEffect(() => {
    applyTheme(pref);
    if (pref !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => applyTheme('system');
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [pref]);
  const next: Record<ThemePreference, ThemePreference> = {
    system: 'light',
    light: 'dark',
    dark: 'system',
  };
  const icons: Record<ThemePreference, string> = {
    light:
      'M12 4V2m0 20v-2m8-8h2M2 12h2m13.7-5.7 1.4-1.4M4.9 19.1l1.4-1.4m0-11.4L4.9 4.9m14.2 14.2-1.4-1.4M12 17a5 5 0 1 0 0-10 5 5 0 0 0 0 10Z',
    dark: 'M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z',
    system: 'M4 5h16v11H4zM8 20h8M12 16v4',
  };
  const label = t('theme.toggle', { mode: t(`theme.${pref}`) });
  return (
    <button
      type="button"
      onClick={() => {
        const n = next[pref];
        setPref(n);
        saveTheme(n);
      }}
      className="rounded-md p-1.5 text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
      aria-label={label}
      title={label}
    >
      <Icon d={icons[pref]} className="size-4.5" />
    </button>
  );
}

function UserMenu() {
  const { t } = useTranslation();
  const me = useMe();
  const logout = useLogout();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const label = me.data?.displayName ?? me.data?.email ?? '';
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="menu"
        className="flex items-center gap-2 rounded-md px-1.5 py-1 hover:bg-slate-100 dark:hover:bg-slate-800"
      >
        <span
          aria-hidden="true"
          className="flex size-7 items-center justify-center rounded-full bg-brand-100 text-xs font-semibold text-brand-800 dark:bg-brand-900 dark:text-brand-100"
        >
          {label.slice(0, 1).toUpperCase()}
        </span>
        <span className="hidden max-w-40 truncate text-sm md:inline">{label}</span>
        <span className="sr-only">{t('nav.userMenu')}</span>
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 z-40 mt-1 w-48 rounded-lg border border-slate-200 bg-white py-1 shadow-lg dark:border-slate-700 dark:bg-slate-900"
          onKeyDown={(e) => e.key === 'Escape' && setOpen(false)}
        >
          <Link
            role="menuitem"
            to="/account"
            className="block px-3 py-1.5 text-sm hover:bg-slate-100 dark:hover:bg-slate-800"
            onClick={() => setOpen(false)}
          >
            {t('nav.account')}
          </Link>
          <Link
            role="menuitem"
            to="/account/billing"
            className="block px-3 py-1.5 text-sm hover:bg-slate-100 dark:hover:bg-slate-800"
            onClick={() => setOpen(false)}
          >
            {t('nav.billing')}
          </Link>
          {me.data?.staffRole && (
            <Link
              role="menuitem"
              to="/staff"
              className="block px-3 py-1.5 text-sm hover:bg-slate-100 dark:hover:bg-slate-800"
              onClick={() => setOpen(false)}
            >
              {t('nav.staffConsole')}
            </Link>
          )}
          <button
            role="menuitem"
            type="button"
            className="block w-full px-3 py-1.5 text-left text-sm hover:bg-slate-100 dark:hover:bg-slate-800"
            onClick={() => logout.mutate(undefined, { onSettled: () => void navigate('/login') })}
          >
            {t('nav.signOut')}
          </button>
        </div>
      )}
    </div>
  );
}
